import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  List,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest } from '../lib/api';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { formatDate } from '../lib/format';
import {
  AptitudeBadge,
  CreateInterviewSlotDrawer,
  EditInterviewSlotDrawer,
  shiftWeek,
  SlotCalendar,
  SLOT_MODE_LABEL,
  SLOT_STATUS_LABEL,
  STAGE_TONE_CLASSES,
  SLOT_STATUS_TONE,
  type InterviewCalendarBooking,
  type InterviewCalendarResponse,
  type InterviewSlot,
} from '../components/hiring';

type ViewMode = 'calendar' | 'list';

export const Route = createFileRoute('/_authed/hr/interviews')({
  component: HiringInterviewsPage,
});

function HiringInterviewsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [view, setView] = useState<ViewMode>('calendar');
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const [createOpen, setCreateOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<InterviewSlot | null>(null);
  // H-4: the calendar defaults to active (open/full) slots only. When this is
  // on we ask the API to include cancelled + completed history too.
  const [showAll, setShowAll] = useState(false);

  // Fetch a 4-week window so calendar nav doesn't re-fetch on every page.
  const range = useMemo(() => {
    const start = new Date(weekAnchor);
    start.setDate(start.getDate() - 28);
    start.setHours(0, 0, 0, 0);
    const end = new Date(weekAnchor);
    end.setDate(end.getDate() + 28);
    end.setHours(23, 59, 59, 999);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [weekAnchor]);

  const calendarQuery = useQuery({
    // H-4: `showAll` is part of the cache key so flipping the toggle never
    // serves a stale active-only payload (or vice versa).
    queryKey: ['hiring', 'interviews', range, showAll] as const,
    queryFn: ({ signal }) =>
      apiRequest<InterviewCalendarResponse>('/api/hiring/interviews', {
        signal,
        query: {
          from: range.from,
          to: range.to,
          // Omit the param by default so the backend's active-only default
          // applies; only opt into history when the toggle is on.
          ...(showAll ? { includeAll: 'true' } : {}),
        },
      }),
  });

  const openDetail = (slot: InterviewSlot) =>
    void navigate({
      to: '/hr/interviews/$slotId',
      params: { slotId: slot.id },
    });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['hiring', 'interviews'] });
    if (editingSlot) {
      void queryClient.invalidateQueries({
        queryKey: ['hiring', 'interview-slot', editingSlot.id],
      });
    }
  };

  const data = calendarQuery.data;

  return (
    <ListOrOutlet basePath="/hr/interviews">
    <ListShell
      title="Interview calendar"
      description="Bookable slots for HR-led candidate interviews. Click a tile to open the slot detail."
      primaryAction={
        <Button
          icon={<CalendarPlus size={16} aria-hidden="true" />}
          onClick={() => setCreateOpen(true)}
        >
          Create slot
        </Button>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="View mode"
            className="inline-flex rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-0.5"
          >
            <button
              type="button"
              onClick={() => setView('calendar')}
              aria-pressed={view === 'calendar'}
              className={
                view === 'calendar'
                  ? 'inline-flex items-center gap-1 rounded-full bg-ink-900 px-3 py-1 text-[11px] font-semibold text-parchment-50'
                  : 'inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold text-[color:var(--color-text-muted)]'
              }
            >
              <Calendar size={11} aria-hidden="true" /> Calendar
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className={
                view === 'list'
                  ? 'inline-flex items-center gap-1 rounded-full bg-ink-900 px-3 py-1 text-[11px] font-semibold text-parchment-50'
                  : 'inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold text-[color:var(--color-text-muted)]'
              }
            >
              <List size={11} aria-hidden="true" /> List
            </button>
          </div>
          {view === 'calendar' ? (
            <WeekNav
              anchor={weekAnchor}
              onShift={(weeks) => setWeekAnchor(shiftWeek(weekAnchor, weeks))}
              onToday={() => setWeekAnchor(new Date())}
            />
          ) : null}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-1 text-[11px] font-semibold text-[color:var(--color-text-muted)]">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="h-3.5 w-3.5 accent-gold-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
            />
            Show cancelled and completed
          </label>
        </div>
      }
    >
      <GatedSection
        error={calendarQuery.error}
        title="Interviews"
        onRetry={() => calendarQuery.refetch()}
      >
        {calendarQuery.isLoading || !data ? (
          <Skeleton className="h-80 w-full" />
        ) : view === 'calendar' ? (
          <SlotCalendar
            slots={data.slots}
            bookings={data.bookings}
            weekStart={weekAnchor}
            onSelect={openDetail}
            onEdit={setEditingSlot}
          />
        ) : (
          <SlotsList
            slots={data.slots}
            bookings={data.bookings}
            onSelect={openDetail}
            onEdit={setEditingSlot}
          />
        )}
      </GatedSection>

      <CreateInterviewSlotDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          invalidate();
        }}
      />

      <EditInterviewSlotDrawer
        open={editingSlot !== null}
        slot={editingSlot}
        onClose={() => setEditingSlot(null)}
        onSaved={() => {
          setEditingSlot(null);
          invalidate();
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

interface WeekNavProps {
  anchor: Date;
  onShift: (weeks: number) => void;
  onToday: () => void;
}

function WeekNav({ anchor, onShift, onToday }: WeekNavProps) {
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-1 py-1">
      <button
        type="button"
        onClick={() => onShift(-1)}
        aria-label="Previous week"
        className="rounded-md p-1 hover:bg-[color:var(--color-surface)]"
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onToday}
        className="rounded-md px-2 py-0.5 text-[11px] font-semibold hover:bg-[color:var(--color-surface)]"
      >
        Today
      </button>
      <span className="px-2 text-xs text-[color:var(--color-text-muted)] tabular-nums">
        {formatDate(anchor.toISOString())}
      </span>
      <button
        type="button"
        onClick={() => onShift(1)}
        aria-label="Next week"
        className="rounded-md p-1 hover:bg-[color:var(--color-surface)]"
      >
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

interface SlotsListProps {
  slots: ReadonlyArray<InterviewSlot>;
  bookings: ReadonlyArray<InterviewCalendarBooking>;
  onSelect: (slot: InterviewSlot) => void;
  onEdit: (slot: InterviewSlot) => void;
}

function SlotsList({ slots, bookings, onSelect, onEdit }: SlotsListProps) {
  if (slots.length === 0) {
    return (
      <EmptyState
        icon={<Calendar size={28} aria-hidden="true" />}
        title="No upcoming slots"
        description="Create your first slot using the button above."
      />
    );
  }
  // Sort chronologically — HR scans the upcoming queue top-to-bottom.
  const sorted = [...slots].sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1));
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <table className="w-full text-sm">
        <thead className="bg-[color:var(--color-surface)]">
          <tr>
            <Th>When</Th>
            <Th>Mode</Th>
            <Th>Status</Th>
            <Th>Candidates</Th>
            <Th>Capacity</Th>
            <Th>Where</Th>
            <Th>
              <span className="sr-only">Edit</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((slot) => {
            // Active bookings drive both the capacity maths and the
            // candidate list HR scans before an interview (H-3).
            const activeBookings = bookings.filter(
              (b) =>
                b.slotId === slot.id &&
                (b.status === 'booked' || b.status === 'attended'),
            );
            return (
              <tr
                key={slot.id}
                onClick={() => onSelect(slot)}
                className="cursor-pointer border-t border-[color:var(--color-border)] align-top transition-colors hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 text-[color:var(--color-text)] tabular-nums">
                  {formatDate(slot.startsAt, { withTime: true })}
                </td>
                <td className="px-4 py-2 text-xs text-[color:var(--color-text-muted)]">
                  {SLOT_MODE_LABEL[slot.mode]}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={clsx(
                      'rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide',
                      STAGE_TONE_CLASSES[SLOT_STATUS_TONE[slot.status]],
                    )}
                  >
                    {SLOT_STATUS_LABEL[slot.status]}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <BookedCandidates bookings={activeBookings} />
                </td>
                <td className="px-4 py-2 tabular-nums">{slot.capacity}</td>
                <td className="px-4 py-2 text-xs text-[color:var(--color-text-muted)] truncate max-w-[20ch]">
                  {slot.locationOrLink ?? '—'}
                </td>
                <td className="px-4 py-2 text-right">
                  <EditIconButton
                    label={`Edit slot ${formatDate(slot.startsAt, { withTime: true })}`}
                    onClick={() => onEdit(slot)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * H-3: per-slot list of booked candidates, each showing the full name (linked
 * through to the application detail) and an aptitude pass/fail badge so HR can
 * prep without an extra click. Renders an em dash for an empty slot.
 */
function BookedCandidates({
  bookings,
}: {
  bookings: ReadonlyArray<InterviewCalendarBooking>;
}) {
  if (bookings.length === 0) {
    return (
      <span className="text-xs text-[color:var(--color-text-subtle)]">—</span>
    );
  }
  return (
    <ul className="space-y-1.5">
      {bookings.map((b) => (
        <li
          key={b.id}
          className="flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          {b.candidate ? (
            <Link
              to="/hr/applications/$applicationId"
              params={{ applicationId: b.candidate.applicationId }}
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-[color:var(--color-text)] hover:underline"
            >
              {b.candidate.fullName}
            </Link>
          ) : (
            <span className="font-mono text-[11px] text-[color:var(--color-text-muted)]">
              {b.applicationId.slice(0, 8)}…
            </span>
          )}
          <AptitudeBadge aptitude={b.candidate?.aptitude ?? null} />
        </li>
      ))}
    </ul>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {children}
    </th>
  );
}
